import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  categoryName: string;
  imageUrl: string;
  imageUrlCompact: string;
  publishedAt: string;
  views: number;
  tags: string[];
  featured?: boolean;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  categoryName,
  imageUrl,
  imageUrlCompact,
  publishedAt,
  views,
  tags,
  featured,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'large') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block relative rounded-lg overflow-hidden cursor-pointer"
      >
        <div className="relative w-full h-[420px] md:h-[500px]">
          <img
            src={imageUrl}
            alt={title}
            title={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-3">
              {categoryName}
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-accent-200 transition-colors line-clamp-2">
              {title}
            </h3>
            <p className="text-white/70 text-sm line-clamp-2 mb-3 max-w-xl">{excerpt}</p>
            <div className="flex items-center gap-3 text-white/60 text-xs">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
      >
        <div className="relative w-full aspect-[4/3] overflow-hidden">
          <img
            src={imageUrlCompact}
            alt={title}
            title={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {featured && (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-background-50">
              热门
            </span>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full">
              {categoryName}
            </span>
            <span className="text-[11px] text-foreground-500">{publishedAt}</span>
          </div>
          <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {title}
          </h3>
          <div className="flex items-center gap-2 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
            </span>
            {tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-secondary-600">#{tag}</span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="relative w-full sm:w-[200px] h-48 sm:h-auto flex-shrink-0 overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          title={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {featured && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-background-50">
            热门
          </span>
        )}
      </div>
      <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full">
              {categoryName}
            </span>
            <span className="text-[11px] text-foreground-500">{publishedAt}</span>
          </div>
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1.5">
            {title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2">{excerpt}</p>
        </div>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-secondary-600">#{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}